import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'store/index';
import { joinDateAndTime } from 'common/helpers/joinDateAndTime';
import dayjs from 'dayjs';
import {
  DrawerSteps,
  RentalDrawerSteps,
} from 'components/Reservation/types/types';
import moment from 'moment';
import { generateRangeMomentArray } from 'common/helpers/generateRangeMomentArray';
import { OrderStatus } from 'types/orders';
import isEmpty from 'lodash.isempty';
import { CarOptionType, ConditionType } from 'store/slices/cars/types';
import { carsSelectors } from 'store/slices/cars/config';
import { isUserAlreadyBooked } from 'common/helpers/IsUserAlreadyBooked';
import { selectSelectedCar } from 'store/slices/cars/selectors';
import {
  selectProfileActiveSubscription,
  selectNewDraftUser,
  selectProfile,
  selectSecondaryDriver,
} from 'store/slices/users/selectors';
import { calculatePrice } from 'components/RentalDrawer/helpers/calculatePrice';
import type { Moment } from 'moment/moment';
import { getDaysCount } from 'components/RentalDrawer/helpers/dateHelpers';

const RentDetailsSelector = (store: RootState) => store.rentDetails;

export const selectRentDate = createSelector(RentDetailsSelector, (state) => {
  return state.rentDate;
});

export const selectRentTime = createSelector(RentDetailsSelector, (state) => {
  return state.rentTime;
});

export const selectCheckedOptions = createSelector(
  RentDetailsSelector,
  (state) => {
    return state.checkedOptions;
  },
);

export const selectTotalPrice = createSelector(RentDetailsSelector, (state) => {
  return state.totalPrice;
});

export const selectPriceForDistance = createSelector(
  RentDetailsSelector,
  (state) => {
    return state.priceForDistance;
  },
);

export const selectPriceForDate = createSelector(
  RentDetailsSelector,
  (state) => {
    return state.priceForDate;
  },
);

export const selectStartLocation = createSelector(
  RentDetailsSelector,
  (state) => {
    return state.startLocation;
  },
);

export const selectEndLocation = createSelector(
  RentDetailsSelector,
  (state) => {
    return state.endLocation;
  },
);

export const selectRentStartDate = createSelector(
  [selectRentDate, selectRentTime],
  (dates, time) => {
    if (!dates || !time.start || !dates[0]) {
      return;
    }
    return joinDateAndTime(dayjs(dates[0]).toDate(), time.start);
  },
);

export const selectRentEndDate = createSelector(
  [selectRentDate, selectRentTime],
  (dates, time) => {
    if (!dates || !time.end || !dates[1]) {
      return;
    }
    return joinDateAndTime(dayjs(dates[1]).toDate(), time.end);
  },
);

export const selectRentalDrawerStep = createSelector(
  RentDetailsSelector,
  (state) => {
    return state.rentalDrawerStep;
  },
);

export const selectCurrentLinkedList = createSelector(
  selectRentalDrawerStep,
  (step) => RentalDrawerSteps[step],
);

export const selectNotesFromCustomer = createSelector(
  RentDetailsSelector,
  (state) => state.notesFromCustomer,
);

export const selectDeliveryKms = createSelector(RentDetailsSelector, (state) =>
  Math.ceil(state.deliveryKms),
);
export const selectRentDetailsErrorFields = createSelector(
  RentDetailsSelector,
  (state) => state.rentalDrawerErrorFields,
);

export const selectTermsAndConditionsAcceptedStatus = createSelector(
  RentDetailsSelector,
  (state) => {
    return state.isTermsAndConditionsAccepted;
  },
);

export const selectIsOrderCreationInProgress = createSelector(
  RentDetailsSelector,
  (state) => {
    return state.isOrderCreationProcess;
  },
);

export const selectDisabledDatesByOrderReason = createSelector(
  [carsSelectors.selectSelectedCar, selectProfile],
  (selectedCar, profile) => {
    if (isEmpty(selectedCar.orders) && isEmpty(profile?.orders)) {
      return new Set([]);
    }

    return new Set(
      [
        ...(selectedCar?.orders || []).filter(
          (item) =>
            ![
              OrderStatus.REFUSED,
              OrderStatus.DONE,
              OrderStatus.ARCHIVED,
              OrderStatus.OUTDATED,
            ].includes(item.status),
        ),
        ...(profile?.orders || []),
      ]
        .map((order) =>
          generateRangeMomentArray(
            order.rentStartDate,
            moment.utc(order.rentEndDate).add(2, 'd').toDate(),
          ),
        )
        .flat(),
    );
  },
);

export const selectInvalidConditions = createSelector(
  [selectSelectedCar, selectNewDraftUser, selectProfile],
  (car, draftUser, profile) => {
    const selectedCarConditions = car?.conditions;
    if (!selectedCarConditions) {
      return [];
    }

    return selectedCarConditions.filter((condition) => {
      const dateValue =
        draftUser?.[
          condition.type === ConditionType.DRIVER_MINIMUM_AGE
            ? 'dateOfBirth'
            : 'licensePermitDate'
        ] ||
        profile?.[
          condition.type === ConditionType.DRIVER_MINIMUM_AGE
            ? 'dateOfBirth'
            : 'licensePermitDate'
        ];
      return moment().diff(moment(dateValue), 'y') < Number(condition.value);
    });
  },
);
export const selectProfileHasCurrentOrder = createSelector(
  [selectProfile, selectRentStartDate, selectRentEndDate],
  (profile, rentStartDate, rentEndDate) => {
    return isUserAlreadyBooked(profile, rentStartDate, rentEndDate);
  },
);

export const selectIsSubscriptionActivated = createSelector(
  RentDetailsSelector,
  (state) => state.isSubscriptionActivated,
);

export const selectOrderPriceForDates = createSelector(
  [
    selectSelectedCar,
    selectPriceForDate,
    selectRentDate,
    selectRentTime,
    selectIsSubscriptionActivated,
  ],
  (car, priceForDate, rentDate, rentTime, isSubscriptionActivated) => {
    if (!car || !car.id) {
      return 0;
    }
    if (isSubscriptionActivated) {
      return 0;
    }
    if (priceForDate && !rentDate) {
      return priceForDate;
    }
    if (!rentDate || !rentDate[0] || !rentDate[1]) {
      return 0;
    }

    return calculatePrice({
      startDate: joinDateAndTime(rentDate[0], rentTime.start),
      endDate: joinDateAndTime(rentDate[1], rentTime.end),
      prices: car.prices,
    });
  },
);

export const selectIsLocationsSelected = createSelector(
  [selectStartLocation, selectEndLocation],
  (startLocation, endLocation) => {
    return Boolean(startLocation && endLocation) || !!startLocation?.trim();
  },
);
export const selectRentalDrawerDisabled = createSelector(
  [
    selectRentalDrawerStep,
    selectSelectedCar,
    selectInvalidConditions,
    selectTermsAndConditionsAcceptedStatus,
    selectProfileHasCurrentOrder,
    selectIsLocationsSelected,
    selectProfileActiveSubscription,
    selectIsSubscriptionActivated,
    selectRentStartDate,
    selectRentEndDate,
  ],
  (
    step,
    selectedCar,
    inValidConditions,
    isTermsAndConditionsAccepted,
    isProfileHasCurrentOrder,
    isLocationsSelected,
    profileActiveSubscription,
    isSubscriptionActivated,
    rentStartDate,
    rentEndDate,
  ) => {
    if (!selectedCar) {
      return true;
    }
    switch (step) {
      case DrawerSteps.CAR_DETAILS: {
        return (
          !selectedCar.isRentable &&
          moment(selectedCar.rentableOnDate).isAfter(moment())
        );
      }

      case DrawerSteps.RESERVATION_DATES: {
        if (selectedCar.onlyWithSubscription && !profileActiveSubscription) {
          return true;
        }
        if (
          profileActiveSubscription &&
          isSubscriptionActivated &&
          rentStartDate &&
          rentEndDate
        ) {
          const daysCount = getDaysCount(rentStartDate, rentEndDate);
          if (
            daysCount >
            profileActiveSubscription.daysCount -
              profileActiveSubscription.daysUsed
          ) {
            return true;
          }
        }

        return !isLocationsSelected || !rentStartDate || !rentEndDate;
      }
      case DrawerSteps.ACCOUNT_CREATION: {
        return (
          inValidConditions.length > 0 ||
          !isTermsAndConditionsAccepted ||
          isProfileHasCurrentOrder
        );
      }

      default: {
        return false;
      }
    }
  },
);

export const selectRentalDaysCount = createSelector(
  [selectRentStartDate, selectRentEndDate],
  (rentStartDate, rentEndDate) => {
    if (!rentStartDate || !rentEndDate) {
      return 0;
    }

    return getDaysCount(rentStartDate as Moment, rentEndDate as Moment);
  },
);
export const selectTotalOptionsPrice = createSelector(
  [
    selectSelectedCar,
    selectCheckedOptions,
    selectRentalDaysCount,
    selectPriceForDistance,
    selectIsSubscriptionActivated,
  ],
  (
    car,
    checkedOptions,
    rentDaysCount,
    priceForDistance,
    isSubscriptionActivated,
  ) => {
    const initialOptions = car?.options || [];
    const options = checkedOptions.REDUCED_DEPOSIT
      ? initialOptions.concat({
          type: CarOptionType.REDUCED_DEPOSIT,
          title: 'Caution réduite',
          value: car.prices.extraFeeIfDepositReduced * rentDaysCount,
        })
      : initialOptions;
    return options
      .map((item) =>
        item.type === CarOptionType.DELIVERY
          ? {
              ...item,
              value: priceForDistance,
            }
          : { ...item, value: isSubscriptionActivated ? 0 : item.value },
      )
      .reduce((accumulator, item) => {
        return checkedOptions[item.type] && item.value !== undefined
          ? accumulator + (item.value as number)
          : accumulator;
      }, 0);
  },
);

export const selectIsSecondaryDriverChecked = createSelector(
  RentDetailsSelector,
  (state) => state.isSecondaryDriverChecked,
);

export const selectSecondDriverInvalidConditions = createSelector(
  [selectSelectedCar, selectSecondaryDriver, selectIsSecondaryDriverChecked],
  (car, secondDriver, isSecondaryDriverChecked) => {
    const selectedCarConditions = car?.conditions;
    if (!selectedCarConditions || !isSecondaryDriverChecked) {
      return [];
    }

    return selectedCarConditions.filter((condition) => {
      const dateValue =
        secondDriver?.[
          condition.type === ConditionType.DRIVER_MINIMUM_AGE
            ? 'dateOfBirth'
            : 'licensePermitDate'
        ];
      return moment().diff(moment(dateValue), 'y') < Number(condition.value);
    });
  },
);

export const selectIsAccountCreationStepPending = createSelector(
  RentDetailsSelector,
  (state) => state.isAccountCreationStepPending,
);
